import React, { useContext, useEffect, useState } from 'react';
import key from 'weak-key';

// styles
import styles from './zip-code-search.module.scss';

// types
import type { ZipCodeSearchViewProps } from './zip-code-search.types';

// components
import { Title } from '../Title/Title';
import { TitleFormats } from '../Title/title.types';
import { Icon } from 'components/Icon/Icon';
import { MailLink } from 'components/Link/MailLink';
import ZipCodeSearchbar from './zip-code-searchbar';
import { ContentContext } from 'components/ContentArea/ContentContext';
import { InlineEdit } from 'components/ContentCreator/InlineEdit';

// utils
import { decodingContent } from 'utils/decodingContent';
import { getSectionId } from 'utils/get-section-id';
import { isEmpty } from 'utils/is-empty';

function ZipCodeSearchView({
  title,
  subtitle,
  inputLabel,
  tableMode,
  headers,
  onSearch,
  border,
  errorMessage = null,
  hits = [],
  anchor = null,
  inputRef,
  contentIndex = null,
  titleInAnchor = null,
}: Readonly<ZipCodeSearchViewProps>) {
  const {
    state: { tocData },
  } = useContext(ContentContext) || {};
  const [sectionId, setSectionId] = useState<null | string>(null);
  const borderClass = border === 'disable-border' ? `${styles.disableBorder}` : '';

  useEffect(() => {
    if (typeof contentIndex === 'number' && tocData && titleInAnchor) {
      const id = getSectionId(anchor ?? title, tocData, contentIndex);
      setSectionId(id);
    }
  }, [anchor, contentIndex, title, titleInAnchor, tocData]);

  const checkEntity = (entity) => {
    if (entity.includes('@')) {
      const content = {
        type: 'mail_link',
        text: entity,
        target: `mailto:${entity}`,
      };

      return (
        <MailLink content={content} className="noArrow">
          <Icon symbol="service-mail" />
        </MailLink>
      );
    }

    if (entity.includes('http')) {
      const symbol = entity.includes('outlook.office.com') ? 'calendar' : 'external-link';
      return (
        <a
          className="noArrow"
          href={entity}
          title={entity}
          aria-label={entity}
          rel="noopener noreferrer"
          target="_blank"
        >
          <Icon symbol={symbol} />
        </a>
      );
    }

    return decodingContent(entity);
  };

  return (
    <div className="grid-container" id={sectionId ?? ''}>
      <div className={`${styles.zipCodeSearchBorder} ${borderClass} grid-x`}>
        <div className={`cell ${styles.zipCodeSearch}`}>
          <Title
            title={title}
            subtitle={subtitle}
            Format={TitleFormats.h2}
            titlePreviewId="#st_title"
            subtitlePreviewId="#st_subtitle"
            formatClassName="h3"
          />

          <ZipCodeSearchbar
            className={styles.bar}
            searchPlaceholder={inputLabel}
            handleSubmit={onSearch}
            inputRef={inputRef}
          />

          {!isEmpty(hits) && (
            <div className="c-table c-table--disable-border">
              <div className="table-outer-wrapper">
                <div className="table-container table-responsive">
                  <table className={`${tableMode} colored-line`}>
                    <thead>
                      <tr>
                        {headers.map((header) => (
                          <th key={key({ header })}>{decodingContent(header)}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {hits?.map((hit) => (
                        <tr key={key({ hit })}>
                          {headers.map((element, index) => (
                            <td key={key({ element })}>{checkEntity(hit[index])}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                    <tfoot />
                  </table>
                </div>
              </div>
            </div>
          )}
          {errorMessage && (
            <p>
              <InlineEdit previewId="#st_error">{decodingContent(errorMessage)}</InlineEdit>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ZipCodeSearchView;
